import { Skeleton, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime, Zone } from 'luxon';
import { CallTranscriptionInfoTooltip } from '../modals/CallTranscriptionModal';

const useStyles = makeStyles((theme: Theme) => ({
  durationItem: {
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: 100,
    margin: '10px',
    color:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.54)'
        : 'rgba(255, 255, 255, 0.64)',
  },
  dateDisplay: {
    color:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.54)'
        : 'rgba(255, 255, 255, 0.64)',
  },
}));

const DurationDisplay = (props: { durationItem: number; type: string }) => {
  const { durationItem, type } = props;
  const classes = useStyles();
  return (
    <span className={classes.durationItem}>
      {type}: {hhmmss(durationItem)}
    </span>
  );
};

export const CallInfo = (props: {
  item: ICallTranscriptionItem;
  zone: string | Zone | undefined;
  loading: boolean;
  callDate: string | undefined;
}) => {
  const { loading } = props;
  const classes = useStyles();
  const contactName =
    !props?.item?.contactFirstName && !props?.item?.contactLastName
      ? 'Target'
      : props?.item?.contactFirstName + ' ' + props?.item?.contactLastName;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ marginTop: 10 }}>
        <div>
          <span
            style={{ textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
          >
            Call with {contactName}
          </span>
          <p className={classes.dateDisplay}>
            {props?.item?.calldate ? (
              <>
                {DateTime.fromISO(props?.item?.calldate)
                  .setZone(props?.zone)
                  .toFormat('LLL dd, yyyy')}
              </>
            ) : (
              <>{props?.callDate}</>
            )}
          </p>
        </div>
        <div style={{ textAlign: 'left', fontSize: '14px', fontWeight: 600 }}>
          Duration
          {loading ? (
            <Skeleton height={17} width={350} variant="rectangular" />
          ) : (
            <>
              <DurationDisplay
                type="Total"
                durationItem={props?.item?.totalDuration}
              />
              <DurationDisplay
                type="Customer"
                durationItem={props?.item?.contactDuration}
              />
              <DurationDisplay
                type="Agent"
                durationItem={props?.item?.agentDuration}
              />
            </>
          )}
          <span style={{ fontSize: '14px' }}>
            <CallTranscriptionInfoTooltip
              title={
                'Shows the total duration of the call, the amount of time the customer spoke, and the amount of time the agent spoke'
              }
            />
          </span>
        </div>
      </div>
    </div>
  );
};

const pad = (num) => {
  if (num > 9) return num;
  return ('0' + num).slice(-2);
};

export const hhmmss = (milliseconds: number) => {
  const negativeDuration = milliseconds < 0;
  if (negativeDuration) {
    milliseconds *= -1;
  }
  let secs = Math.floor((milliseconds ?? 0) / 1000);
  let mins = Math.floor(secs / 60);
  const hours = Math.floor(mins / 60);
  secs = Math.floor(secs % 60);
  if (hours > 0) {
    mins = Math.floor(mins % 60);
    return (
      (negativeDuration ? '-' : '') + `${pad(hours)}:${pad(mins)}:${pad(secs)}`
    );
  }
  return (negativeDuration ? '-' : '') + `${pad(mins)}:${pad(secs)}`;
};

export interface IUtterance {
  channel: string;
  text: string;
  confidence: number;
  start: number;
  end: number;
  words: any[];
  speaker: string;
}

export interface ISentimentAnalysisResult {
  text: string;
  start: number;
  end: number;
  sentiment: string;
  confidence: number;
  speaker: string;
}

export interface ICallTranscriptionItem {
  transcriptionXd: string;
  totalDuration: number;
  contactDuration: number;
  createdDate: Date;
  businessId: number;
  callId: string;
  calldate: string;
  contactLastName: string;
  recordingUrl: string;
  callXd: string;
  callStrength: number;
  keywords: Record<string, boolean>;
  sk: string;
  itemType: string;
  contactFirstName: string;
  pk: string;
  updatedDate: Date;
  agentDuration: number;
  phrases: Record<string, boolean>;
  agentMonologue: number;
  patience: number;
  contactStory: number;
  sentiment: string;
  speakers: string[];
  text: string | null;
  sentimentAnalysisResults: ISentimentAnalysisResult[] | null;
  summary: string;
  utterances: IUtterance[] | null;
  languageCode?: string;
}
